/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-07 14:48:17
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-09-01 10:20:42
 */
import React, { useState, useEffect, ChangeEvent } from 'react'
import { Select, Input, Button, Divider } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import DatesPicker from '../../components/DatesPickerV2'
import DepartmentMemberPicker from '../../components/DepartmentMemberPickerV2'
import useRectifyAnalytics from '../hooks/useRectifyAnalytics'
import { DownOutlined } from '@ant-design/icons'

const { Option } = Select

// 用户类型
interface DepartmentUser {
  userID: string | number
  [key: string]: any
}

// 部门类型
interface Department {
  [key: string]: any
}

// 人员选择器回调参数
interface MemberPickerParams {
  selectedDepartmentUsers: DepartmentUser[]
  selectedDepartments: Department[]
}

// 筛选状态类型
interface FilterState {
  groupID: string | number | null
  userID: string
  times: [Dayjs, Dayjs]
  status: number
  createUserIDs: (string | number)[]
  rectificationUserIDs: (string | number)[]
  departments: Department[]
  departmentUsers: DepartmentUser[]
  creatorUsers: DepartmentUser[]
  rectificationUsers: DepartmentUser[]
  creatorDepartments: Department[]
  rectificationDepartments: Department[]
  page: number
  pageSize: number
  recordNo: number | undefined
}

// 整改数据类型
interface RectifyData {
  totalCount?: number
  rectifiedCount?: number
  unRectifiedCount?: number
  [key: string]: any
}

// Props 类型
interface FilterProps {
  onFilter: (params: any, callback?: (data: any) => void) => void
  groupID: string | number | null
  data: RectifyData
  initialRecordNo?: number | null
}

const Filter: React.FC<FilterProps> = (props) => {
  const { onFilter, groupID, data, initialRecordNo } = props

  // 使用埋点hook
  const { trackRectifyEvent, getSourceTypeByStatus } = useRectifyAnalytics()

  // 最小可选日期 - 2024年8月1日
  const minSelectableDate = dayjs('2024-08-01')

  // 筛选条件
  const [filter, setFilter] = useState<FilterState>({
    groupID: groupID,
    userID: '',
    times: [
      minSelectableDate, // 使用minSelectableDate作为起始时间
      dayjs().endOf('day') // 到今天结束
    ] as [Dayjs, Dayjs],
    status: 0, // 默认全部
    createUserIDs: [],
    rectificationUserIDs: [],
    departments: [],
    departmentUsers: [],
    // 存储完整的用户对象
    creatorUsers: [],
    rectificationUsers: [],
    // 部门信息
    creatorDepartments: [],
    rectificationDepartments: [],
    page: 1,
    pageSize: 10,
    recordNo: undefined
  })

  // 弹窗状态
  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [showCreatorPickerModal, setShowCreatorPickerModal] = useState(false)
  const [
    showRectificationUserPickerModal,
    setShowRectificationUserPickerModal
  ] = useState(false)

  // 状态选项
  const statusOptions = [
    { value: 0, label: '全部任务' },
    { value: 1, label: '未完成' },
    { value: 2, label: '已完成' }
  ]

  // 禁用2024年8月之前的日期
  const disabledDate = (current: Dayjs) => {
    return current && current.isBefore(minSelectableDate, 'day')
  }

  // 获取当前有效的数据统计信息
  const getValidData = () => {
    if (!data) return {}

    return {
      totalCount: data.totalCount || 0,
      rectifiedCount: data.rectifiedCount || 0,
      unRectifiedCount: data.unRectifiedCount || 0
    }
  }

  // 处理日期范围变化
  const handleDatesChange = (startDate: Dayjs, endDate: Dayjs) => {
    setShowDatePickerPane(false)
    if (startDate.isAfter(endDate)) {
      setFilter({ ...filter, times: [endDate, startDate] })
    } else {
      setFilter({ ...filter, times: [startDate, endDate] })
    }

    // 上报日期筛选埋点
    trackRectifyEvent('clickFilterDate', getValidData(), {
      startDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
      endDate: endDate.format('YYYY-MM-DD HH:mm:ss')
    })
  }

  // 处理状态变化
  const handleStatusChange = (value: number) => {
    setFilter({ ...filter, status: value })

    // 上报状态筛选埋点
    trackRectifyEvent('clickFilterType', getValidData(), {
      sourceType: getSourceTypeByStatus(value)
    })
  }

  // 处理发起人变化
  const handleCreatorChange = (params: MemberPickerParams) => {
    const { selectedDepartmentUsers, selectedDepartments } = params
    setShowCreatorPickerModal(false)

    // 提取用户ID列表
    const userIDs = selectedDepartmentUsers.map((user) => user.userID)

    setFilter({
      ...filter,
      createUserIDs: userIDs,
      creatorDepartments: selectedDepartments,
      // 保存完整的用户对象数据
      creatorUsers: selectedDepartmentUsers
    })

    // 上报发起人筛选埋点
    trackRectifyEvent('clickFilterFounder', getValidData())
  }

  // 处理整改人变化
  const handleRectificationUserChange = (params: MemberPickerParams) => {
    const { selectedDepartmentUsers, selectedDepartments } = params
    setShowRectificationUserPickerModal(false)

    // 提取用户ID列表
    const userIDs = selectedDepartmentUsers.map((user) => user.userID)

    setFilter({
      ...filter,
      rectificationUserIDs: userIDs,
      rectificationDepartments: selectedDepartments,
      // 保存完整的用户对象数据
      rectificationUsers: selectedDepartmentUsers
    })

    // 上报待办人筛选埋点
    trackRectifyEvent('clickFilterRectifier', getValidData())
  }

  // 处理编号搜索变化
  const handleRecordNoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? Number(e.target.value) : undefined
    setFilter({ ...filter, recordNo: value })
  }

  // 提取共享的请求参数构建逻辑
  const buildFilterParams = () => {
    return {
      groupID: filter.groupID,
      userID: filter.userID,
      startTime: filter.times[0].format('YYYY-MM-DD HH:mm:ss'),
      endTime: filter.times[1].format('YYYY-MM-DD HH:mm:ss'),
      status: filter.status,
      createUserIDs: filter.createUserIDs,
      rectificationUserIDs: filter.rectificationUserIDs,
      page: filter.page,
      pageSize: filter.pageSize,
      recordNo: filter.recordNo
    }
  }

  // 提交筛选
  const handleSubmit = () => {
    // 关闭所有弹窗
    closeAllModals()

    // 构建API请求参数
    const filterParams = buildFilterParams()

    // 先调用筛选，获取最新数据后再上报埋点
    onFilter(filterParams, (latestData) => {
      const trackData = {
        totalCount: latestData?.totalCount || 0,
        rectifiedCount: latestData?.rectifiedCount || 0,
        unRectifiedCount: latestData?.unRectifiedCount || 0
      }

      trackRectifyEvent('clickFilter', trackData, {
        startDate: filterParams.startTime,
        endDate: filterParams.endTime,
        sourceType: getSourceTypeByStatus(filterParams.status)
      })
    })
  }

  // 重置筛选
  const handleReset = () => {
    const resetFilter: FilterState = {
      groupID: groupID,
      userID: '',
      times: [minSelectableDate, dayjs().endOf('day')] as [Dayjs, Dayjs],
      status: 0,
      createUserIDs: [],
      rectificationUserIDs: [],
      departments: [],
      departmentUsers: [],
      creatorDepartments: [],
      rectificationDepartments: [],
      creatorUsers: [],
      rectificationUsers: [],
      page: 1,
      pageSize: 10,
      recordNo: undefined
    }

    setFilter(resetFilter)

    // 构建重置后的筛选参数
    const filterParams = {
      groupID: resetFilter.groupID,
      userID: resetFilter.userID,
      startTime: resetFilter.times[0].format('YYYY-MM-DD HH:mm:ss'),
      endTime: resetFilter.times[1].format('YYYY-MM-DD HH:mm:ss'),
      status: resetFilter.status,
      createUserIDs: [],
      rectificationUserIDs: [],
      page: resetFilter.page,
      pageSize: resetFilter.pageSize,
      recordNo: resetFilter.recordNo
    }

    // 先调用筛选，获取最新数据后再上报埋点
    onFilter(filterParams, (latestData) => {
      // 使用最新数据构建埋点数据
      const trackData = {
        totalCount: latestData?.totalCount || 0,
        rectifiedCount: latestData?.rectifiedCount || 0,
        unRectifiedCount: latestData?.unRectifiedCount || 0
      }

      trackRectifyEvent('clickReset', trackData)
    })
  }

  // 计算已选人员数量和显示文本
  const calcMemberDisplay = (type: 'creator' | 'rectification') => {
    const users =
      type === 'creator' ? filter.creatorUsers : filter.rectificationUsers

    // 计算选择的成员数量
    const count = users ? users.length : 0

    if (!users || count === 0) {
      return '全部成员'
    }

    return `已选${count}人`
  }

  // 创建发起人筛选用的filterObj
  const createCreatorFilterObj = () => {
    return {
      departments: filter.creatorDepartments || [],
      departmentUsers: filter.creatorUsers || []
    }
  }

  // 创建整改人筛选用的filterObj
  const createRectificationUserFilterObj = () => {
    return {
      departments: filter.rectificationDepartments || [],
      departmentUsers: filter.rectificationUsers || []
    }
  }

  // 渲染时间筛选区域
  const renderTimePickerArea = () => {
    const startTime = filter.times[0]
    const endTime = filter.times[1]

    const startTimeFormatted = dayjs().isSame(startTime, 'year')
      ? startTime.format('M月D日')
      : startTime.format('YY年M月D日')

    const endTimeFormatted = dayjs().isSame(endTime, 'year')
      ? endTime.format('M月D日')
      : endTime.format('YY年M月D日')

    return (
      <div className="filter-item">
        <span className="filter-label">筛选时间</span>
        <div
          className="filter-dropdown"
          style={{ minWidth: 155 }}
          onClick={() => setShowDatePickerPane(true)}
        >
          <span className="value">
            {startTimeFormatted + '-' + endTimeFormatted}
          </span>
          <DownOutlined />
        </div>

        {/* 时间选择器弹窗 */}
        <DatesPicker
          isAllPhoto={true}
          visible={showDatePickerPane}
          dates={filter.times}
          onChange={handleDatesChange}
          onCancel={() => setShowDatePickerPane(false)}
          disabledDate={disabledDate}
        />
      </div>
    )
  }

  // 初始化加载数据，不触发埋点
  const initializeFilter = () => {
    // 关闭所有弹窗
    closeAllModals()

    // 构建API请求参数
    const filterParams = buildFilterParams()

    // 只调用筛选，不上报埋点
    onFilter(filterParams)
  }

  // 监听initialRecordNo变化，设置到filter中并触发筛选
  useEffect(() => {
    if (initialRecordNo) {
      // 设置recordNo到filter中
      setFilter((prevFilter) => ({ ...prevFilter, recordNo: initialRecordNo }))

      // 构建筛选参数并执行筛选
      const filterParams = {
        groupID: groupID,
        userID: '',
        startTime: minSelectableDate.format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        status: 0,
        createUserIDs: [],
        rectificationUserIDs: [],
        page: 1,
        pageSize: 10,
        recordNo: initialRecordNo
      }

      // 触发筛选
      onFilter(filterParams)
    }
  }, [initialRecordNo])

  // 组件挂载时提交一次筛选
  useEffect(() => {
    // 检查URL是否有recordID参数，如果有就不要默认请求
    const urlParams = new URLSearchParams(window.location.search)
    const hasRecordID = urlParams.has('recordID')

    if (!hasRecordID) {
      initializeFilter() // 使用新的初始化函数
    }
  }, [])

  // 关闭所有弹窗的辅助函数
  const closeAllModals = () => {
    setShowDatePickerPane(false)
    setShowCreatorPickerModal(false)
    setShowRectificationUserPickerModal(false)
  }

  return (
    <div className="rectify-filter-wrapper">
      <div className="filter-container">
        {/* 时间筛选 */}
        {renderTimePickerArea()}

        <Divider orientation="vertical" />

        {/* 任务状态筛选 */}
        <div className="filter-item">
          <span className="filter-label">任务状态</span>
          <Select
            value={filter.status}
            onChange={handleStatusChange}
            className="filter-select"
            style={{ width: 95 }}
            suffixIcon={<DownOutlined />}
            onClick={() => {
              // 上报点击筛选任务状态埋点
              trackRectifyEvent('clickFilterType', getValidData())
            }}
          >
            {statusOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>

        <Divider orientation="vertical" />

        {/* 发起人筛选 */}
        <div className="filter-item">
          <span className="filter-label">发起人</span>
          <div
            className="filter-dropdown"
            onClick={() => setShowCreatorPickerModal(true)}
          >
            <span className="value">{calcMemberDisplay('creator')}</span>
            <DownOutlined />
          </div>
        </div>

        <Divider orientation="vertical" />

        {/* 整改人筛选 */}
        <div className="filter-item">
          <span className="filter-label">待办人</span>
          <div
            className="filter-dropdown"
            onClick={() => setShowRectificationUserPickerModal(true)}
          >
            <span className="value">{calcMemberDisplay('rectification')}</span>
            <DownOutlined />
          </div>
        </div>

        <Divider orientation="vertical" />

        {/* 任务编号搜索 */}
        <div className="filter-item task-id-input">
          <span className="filter-label">任务编号</span>
          <Input
            placeholder="请输入编号"
            onChange={handleRecordNoChange}
            value={filter.recordNo}
            style={{ width: 85, border: 'none', boxShadow: 'none' }}
            onBlur={() => {
              // 上报点击筛选任务编号埋点
              trackRectifyEvent('clickSerachNo', getValidData())
            }}
          />
        </div>
      </div>

      <div>
        <Button size="large" onClick={handleReset}>
          重置
        </Button>
        <Button
          size="large"
          type="primary"
          style={{ width: '70px', marginLeft: '12px' }}
          onClick={handleSubmit}
        >
          筛选
        </Button>
      </div>

      {/* 人员选择器弹窗 - 发起人 */}
      <DepartmentMemberPicker
        open={showCreatorPickerModal}
        filterObj={createCreatorFilterObj() as any}
        onOk={handleCreatorChange}
        onCancel={() => setShowCreatorPickerModal(false)}
        onlyRootDepartments={true}
      />

      {/* 人员选择器弹窗 - 整改人 */}
      <DepartmentMemberPicker
        open={showRectificationUserPickerModal}
        filterObj={createRectificationUserFilterObj() as any}
        onOk={handleRectificationUserChange}
        onCancel={() => setShowRectificationUserPickerModal(false)}
        onlyRootDepartments={true}
      />
    </div>
  )
}

export default Filter
